import { useEffect, useMemo, useState } from "react";
import api from "@/lib/api";
import EmptyState from "@/components/EmptyState";
import { Mic, Play } from "lucide-react";

const KIND_LABEL = {
    good: "Good call", bad: "Bad call", improved: "Improved",
    objection: "Objection", voicemail: "Voicemail", discovery: "Discovery",
};

export default function Roleplays() {
    const [items, setItems] = useState([]);
    const [kind, setKind] = useState("All");
    const [open, setOpen] = useState(null);

    useEffect(() => {
        api.get("/roleplays").then((r) => setItems(r.data));
    }, []);

    const kinds = useMemo(() => ["All", ...Array.from(new Set(items.map((i) => i.kind).filter(Boolean)))], [items]);
    const filtered = kind === "All" ? items : items.filter((i) => i.kind === kind);

    return (
        <div className="space-y-10" data-testid="roleplays-page">
            <div>
                <div className="text-[10px] uppercase tracking-[0.25em] text-ink-400 font-mono mb-3">Audio library</div>
                <h1 className="font-editorial text-4xl md:text-5xl text-ink-900 leading-[1.05]">Roleplay Library.</h1>
                <p className="mt-3 text-base text-ink-500 max-w-xl">Listen to real conversation patterns — the good, the bad, and the corrected.</p>
            </div>

            <div className="flex flex-wrap gap-2">
                {kinds.map((c) => (
                    <button
                        key={c}
                        onClick={() => setKind(c)}
                        data-testid={`roleplay-kind-${c}`}
                        className={`px-3 py-1.5 text-xs font-mono uppercase tracking-[0.15em] rounded-sm border transition-colors ${
                            kind === c ? "bg-haevn-700 text-white border-haevn-700" : "bg-white text-ink-500 border-ink-300 hover:border-haevn-700 hover:text-ink-900"
                        }`}
                    >{KIND_LABEL[c] ?? c}</button>
                ))}
            </div>

            {items.length === 0 ? (
                <EmptyState title="No roleplays yet" description="Upload audio examples in the Admin panel." icon={Mic} testId="roleplays-empty" />
            ) : (
                <div className="grid md:grid-cols-2 gap-px bg-ink-300 border border-ink-300">
                    {filtered.map((r) => (
                        <button
                            key={r.id}
                            onClick={() => setOpen(r)}
                            data-testid={`roleplay-${r.id}`}
                            className="bg-white p-6 text-left hover:bg-ink-100/60 transition-colors group"
                        >
                            <div className="flex items-start justify-between">
                                <div className="text-[10px] uppercase tracking-[0.2em] text-ink-400 font-mono">{KIND_LABEL[r.kind] ?? r.kind}</div>
                                <Play size={14} strokeWidth={1.5} className="text-ink-400 group-hover:text-haevn-700 transition-colors" />
                            </div>
                            <div className="mt-2 font-heading text-lg text-ink-900 group-hover:text-haevn-700 transition-colors">{r.title}</div>
                            {r.description && <div className="mt-1 text-sm text-ink-500 line-clamp-2">{r.description}</div>}
                            {r.tags?.length > 0 && (
                                <div className="mt-3 flex flex-wrap gap-1.5">
                                    {r.tags.slice(0, 4).map((t) => (
                                        <span key={t} className="text-[10px] font-mono uppercase tracking-[0.15em] text-ink-500 bg-ink-100 px-1.5 py-0.5">{t}</span>
                                    ))}
                                </div>
                            )}
                        </button>
                    ))}
                </div>
            )}

            {open && (
                <div className="fixed inset-0 z-40 bg-ink-900/40 backdrop-blur-sm flex items-center justify-center p-6" onClick={() => setOpen(null)}>
                    <div className="bg-white border border-ink-300 max-w-3xl w-full max-h-[85vh] overflow-y-auto p-8 md:p-10" onClick={(e) => e.stopPropagation()} data-testid="roleplay-modal">
                        <div className="text-[10px] uppercase tracking-[0.2em] text-ink-400 font-mono">{KIND_LABEL[open.kind] ?? open.kind}</div>
                        <h2 className="font-editorial text-3xl mt-2">{open.title}</h2>
                        {open.description && <p className="text-sm text-ink-500 mt-2">{open.description}</p>}
                        {open.audio_url ? (
                            <audio controls src={open.audio_url} className="mt-6 w-full" data-testid="roleplay-audio" />
                        ) : (
                            <div className="mt-6 border border-dashed border-ink-300 p-10 text-center text-sm text-ink-500">No audio attached.</div>
                        )}
                        {open.takeaways?.length > 0 && (
                            <div className="mt-6">
                                <div className="text-[10px] uppercase tracking-[0.2em] text-ink-400 font-mono mb-2">Key takeaways</div>
                                <ul className="space-y-1.5 text-sm text-ink-700">
                                    {open.takeaways.map((t, i) => <li key={i} className="flex gap-2"><span className="text-ink-400">·</span> {t}</li>)}
                                </ul>
                            </div>
                        )}
                        {open.transcript && (
                            <details className="mt-6 group">
                                <summary className="cursor-pointer text-xs font-mono uppercase tracking-[0.15em] text-ink-500 hover:text-ink-900">Transcript</summary>
                                <div className="mt-3 text-sm text-ink-700 leading-relaxed whitespace-pre-line">{open.transcript}</div>
                            </details>
                        )}
                        <button onClick={() => setOpen(null)} className="mt-6 border border-ink-300 px-5 py-2 rounded-sm text-sm hover:border-haevn-700">Close</button>
                    </div>
                </div>
            )}
        </div>
    );
}
